import { Box, Heading, Text, TextButton } from '@wix/design-system'
import React, { ComponentProps, FC, memo, ReactNode, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'

import { PageLogger } from '../../../bi/mfi'
import { HEADLESS_MOBILE } from '../../../breakpoints'
import { BackButton } from '../BackButton'
import { HeadlessBackground } from '../HeadlessBackground'
import WixStudioLogo from '../icons/WixStudioLogo.svg'
import { LogoWithBack } from '../LogoWithBack'
import { SkipButton } from '../SkipButton'
import { headlessTemplateDataHooks as dataHooks } from './HeadlessTemplate.dataHooks'
import s from './HeadlessTemplate.scss'

type Props = {
  isPageLoading?: boolean
  pageLogger: PageLogger
  noScreenViewBI?: boolean

  stepText: string
  title: string
  subtitle: string
  cardTitle?: string

  backButton?: string
  skipButton?: string
  onSkipClick?: () => void

  cardMarginTop?: ComponentProps<typeof Box>['marginTop']

  dataHook?: string
  children?: ReactNode
}

export const HeadlessTemplate: FC<Props> = memo(props => {
  const {
    isPageLoading,
    pageLogger,
    noScreenViewBI,
    stepText,
    title,
    subtitle,
    cardTitle,
    children,
    backButton,
    skipButton,
    onSkipClick,
    cardMarginTop = '124px',
    dataHook,
  } = props

  const isMobileView = useMediaQuery(HEADLESS_MOBILE)
  useEffect(() => {
    if (noScreenViewBI) {
      return
    }
    void pageLogger.screenView()
  }, [noScreenViewBI, pageLogger])

  const handleSkipClick = () => {
    if (isPageLoading) {
      return
    }
    void pageLogger.skip()

    onSkipClick?.()
  }
  const handleBackClick = () => {
    if (isPageLoading) {
      return
    }
    void pageLogger.back()

    window.history.back()
  }

  const titleAndSubtitle = (
    <Box className={s.titleAndSubtitle}>
      {isMobileView && <WixStudioLogo fill="#fff" height={25} />}
      {!isMobileView && (
        <Box marginBottom="18px">
          <Text size="small" weight="thin" light dataHook={dataHooks.stepText()}>
            {stepText}
          </Text>
        </Box>
      )}
      <Heading size="extraLarge" light className={s.title} dataHook={dataHooks.title()}>
        {title}
      </Heading>
      <Heading size="medium" light className={s.subtitle} dataHook={dataHooks.subtitle()}>
        {subtitle}
      </Heading>
    </Box>
  )

  return (
    <HeadlessBackground dataHook={dataHook}>
      {isMobileView && (
        <Box>
          {backButton && (
            <BackButton text={backButton} onClick={handleBackClick} skin="light" dataHook={dataHooks.back()} />
          )}
          {stepText && (
            <Box marginLeft="auto">
              <Text size="small" weight="thin" light dataHook={dataHooks.stepText()}>
                {stepText}
              </Text>
            </Box>
          )}
        </Box>
      )}
      {!isMobileView && <LogoWithBack skin="light" brand="studio" height="28px" width="175px" />}
      {titleAndSubtitle}
      <Box className={s.card} marginTop={cardMarginTop} maxHeight={`calc(100% - ${cardMarginTop})`}>
        {cardTitle && (
          <Box className={s.cardTitle}>
            <Heading size="medium" dataHook={dataHooks.cardTitle()}>
              {cardTitle}
            </Heading>
          </Box>
        )}
        {children}
        {isMobileView && skipButton && (
          <Box align="center" marginTop="30px">
            <TextButton onClick={handleSkipClick} dataHook={dataHook} skin="dark" size="small">
              {skipButton}
            </TextButton>
          </Box>
        )}
      </Box>
      {!isMobileView && backButton && (
        <Box position="absolute" left="0" bottom="0" padding="36px">
          <BackButton text={backButton} onClick={handleBackClick} skin="light" dataHook={dataHooks.back()} />
        </Box>
      )}
      {!isMobileView && skipButton && (
        <SkipButton
          skipButton={skipButton}
          onClick={handleSkipClick}
          position="bottomRight"
          skin="light"
          dataHook={dataHooks.skip()}
        />
      )}
    </HeadlessBackground>
  )
})
